import * as THREE from "three";
import { gerstnerHeight } from "../ocean";
import { PALETTE } from "../config";
import type { ClubMaterials } from "../materials";

const PATH: Array<[number, number]> = [
  [-20, 6],
  [-8, 14],
  [10, 12],
  [18, 2],
  [6, -12],
  [-10, -8],
];

/** Compact WasteShark-style ASV that patrols the harbour. */
export class WasteShark {
  readonly group: THREE.Group;
  private seg = 0;
  private u = 0;
  private heading = 0;

  constructor(mats: ClubMaterials) {
    this.group = buildWasteShark(mats);
    this.group.name = "wasteshark";
    this.group.position.set(PATH[0][0], 0.08, PATH[0][1]);
  }

  update(t: number, dt: number): void {
    const a = PATH[this.seg];
    const b = PATH[(this.seg + 1) % PATH.length];
    const dx = b[0] - a[0];
    const dz = b[1] - a[1];
    const span = Math.hypot(dx, dz) || 1;
    this.u += (0.7 * dt) / span;
    while (this.u >= 1) {
      this.u -= 1;
      this.seg = (this.seg + 1) % PATH.length;
    }
    const from = PATH[this.seg];
    const to = PATH[(this.seg + 1) % PATH.length];
    const x = from[0] + (to[0] - from[0]) * this.u;
    const z = from[1] + (to[1] - from[1]) * this.u;
    this.group.position.set(x, gerstnerHeight(x, z, t) + 0.06, z);
    const want = Math.atan2(to[0] - from[0], to[1] - from[1]);
    this.heading = THREE.MathUtils.lerp(this.heading, want, 1 - Math.exp(-5 * dt));
    this.group.rotation.y = this.heading;
    this.group.rotation.z = Math.sin(t * 1.6) * 0.04;
  }
}

function buildWasteShark(mats: ClubMaterials): THREE.Group {
  const g = new THREE.Group();
  const orange = new THREE.MeshStandardMaterial({
    color: PALETTE.coral,
    roughness: 0.38,
    metalness: 0.08,
  });
  const hullL = new THREE.Mesh(new THREE.BoxGeometry(0.42, 0.28, 1.7), orange);
  hullL.position.set(-0.48, 0.08, 0);
  hullL.castShadow = true;
  const hullR = hullL.clone();
  hullR.position.x = 0.48;
  g.add(hullL, hullR);

  const bowL = new THREE.Mesh(new THREE.BoxGeometry(0.38, 0.22, 0.55), orange);
  bowL.position.set(-0.48, 0.06, 0.95);
  bowL.rotation.x = -0.18;
  const bowR = bowL.clone();
  bowR.position.x = 0.48;
  g.add(bowL, bowR);

  const deck = new THREE.Mesh(new THREE.BoxGeometry(1.15, 0.08, 1.35), mats.white);
  deck.position.set(0, 0.24, -0.05);
  g.add(deck);

  const mouth = new THREE.Mesh(new THREE.BoxGeometry(0.85, 0.22, 0.7), mats.navy);
  mouth.position.set(0, 0.05, 0.55);
  g.add(mouth);

  const basket = new THREE.Mesh(
    new THREE.BoxGeometry(0.78, 0.28, 0.55),
    new THREE.MeshStandardMaterial({
      color: 0x8aa0aa,
      roughness: 0.5,
      metalness: 0.35,
      wireframe: true,
    }),
  );
  basket.position.set(0, 0.12, 0.55);
  g.add(basket);

  const solar = new THREE.Mesh(
    new THREE.BoxGeometry(0.7, 0.03, 0.9),
    new THREE.MeshStandardMaterial({ color: 0x163043, roughness: 0.25, metalness: 0.4 }),
  );
  solar.position.set(0, 0.3, -0.15);
  g.add(solar);

  const mast = new THREE.Mesh(new THREE.CylinderGeometry(0.025, 0.03, 0.55, 8), mats.chrome);
  mast.position.set(0, 0.55, -0.2);
  g.add(mast);
  const lamp = new THREE.Mesh(new THREE.SphereGeometry(0.06, 8, 6), mats.white);
  lamp.position.set(0, 0.84, -0.2);
  g.add(lamp);

  g.scale.setScalar(1.15);
  return g;
}
